import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./ChatModal.scss";
import useChatStore from "../../../store/chatStore";
import useAuthStore from "../../../store/authStore";
import { getChatContacts } from "../../../api/messages";
import { useSocket } from "../../../context/SocketContext";
import EmojiPicker from "emoji-picker-react";
import {
  replaceEmoticons,
  replaceTypedEmoticon,
} from "../../../utils/emoticonExtension";
import { GiphyFetch } from "@giphy/js-fetch-api";
import { Grid } from "@giphy/react-components";

// Using Giphy's public demo API key - replace with your own in production
const gf = new GiphyFetch("sXpGFDGZs0Dv1mmNFvYaGUvYwKX0PWIh");

const ChatModal = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifSearchTerm, setGifSearchTerm] = useState("");
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);
  // Organization names are only shown when the user is in more than one
  const [organizationCount, setOrganizationCount] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const { token, user } = useAuthStore();
  const { joinConversation, leaveConversation } = useSocket();
  const {
    conversations,
    messages,
    fetchConversations,
    fetchMessages,
    sendMessage: sendMessageToStore,
    getOrCreateDirectConversation,
    getOrCreateTeamConversation,
    setSelectedConversation,
    cleanupOrphanedConversations,
  } = useChatStore();

  // Fetch conversations, teams, and members on mount
  useEffect(() => {
    const loadData = async () => {
      if (!token) return;

      try {
        // Don't fetch conversations here - they're already loaded in App.jsx
        // and fetching them again would show updated unread counts from the server

        // Everyone to chat with, across all the user's organizations (chat
        // isn't tied to one organization)
        const contacts = await getChatContacts(token);
        setTeams(contacts.teams || []);
        setMembers(contacts.people || []);
        setOrganizationCount(contacts.organizationCount || 0);

        // Mark data as loaded only after BOTH teams and members are fetched
        setDataLoaded(true);
      } catch (error) {
        console.error("Error loading chat data:", error);
      }
    };

    loadData();
  }, [token, user]);

  // Clean up orphaned conversations when teams/members change
  useEffect(() => {
    // Only run cleanup after data has been fully loaded
    // This prevents removing conversations when teams are loaded but members aren't yet
    if (!dataLoaded) return;

    const validTeamIds = teams.map((t) => t.id);
    const validMemberIds = members.map((m) => m.id);

    cleanupOrphanedConversations(validTeamIds, validMemberIds);
  }, [dataLoaded, teams, members, cleanupOrphanedConversations]);

  // Join/leave conversation room when selected chat changes
  useEffect(() => {
    if (selectedChat?.conversationId) {
      joinConversation(selectedChat.conversationId);

      return () => {
        leaveConversation(selectedChat.conversationId);
      };
    }
  }, [selectedChat, joinConversation, leaveConversation]);

  // Scroll to bottom when messages change or chat is selected
  useEffect(() => {
    if (selectedChat) {
      // Use setTimeout to ensure DOM is updated before scrolling
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [messages, selectedChat]);

  // Clear selected conversation when modal closes
  useEffect(() => {
    return () => {
      setSelectedConversation(null);
    };
  }, [setSelectedConversation]);

  // Helper function to check if a text contains a GIF URL
  const isGifUrl = (text) => {
    return (
      text &&
      (text.includes("giphy.com") ||
        text.includes("tenor.com") ||
        text.match(/\.(gif|webp)$/i))
    );
  };

  // Helper function to render message content (text or GIF)
  const renderMessageContent = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = text.match(urlRegex);

    if (urls && urls.some((url) => isGifUrl(url))) {
      // Extract text and GIF URL
      const parts = text.split(urlRegex);
      return parts.map((part, index) => {
        if (isGifUrl(part)) {
          return (
            <img
              key={index}
              src={part}
              alt="GIF"
              className="message-gif"
              style={{
                maxWidth: "250px",
                borderRadius: "8px",
                marginTop: "4px",
              }}
            />
          );
        }
        return part ? <span key={index}>{part}</span> : null;
      });
    }
    return text;
  };

  // Put the cursor back after React has updated the input
  const setMessageWithCursor = (text, cursor) => {
    setMessage(text);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(cursor, cursor);
    });
  };

  // Typed emoticons like :) become emojis when a space follows (as in notes)
  const handleMessageChange = (e) => {
    const { value, selectionStart } = e.target;
    const replaced = replaceTypedEmoticon(value, selectionStart);
    if (replaced) {
      setMessageWithCursor(replaced.text, replaced.cursor);
    } else {
      setMessage(value);
    }
  };

  // Emojis from the picker go in at the cursor
  const insertEmoji = (emoji) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? message.length;
    const end = input?.selectionEnd ?? message.length;
    setMessageWithCursor(
      message.slice(0, start) + emoji + message.slice(end),
      start + emoji.length,
    );
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedChat?.conversationId) return;

    try {
      await sendMessageToStore(
        selectedChat.conversationId,
        replaceEmoticons(message),
        token,
      );
      setMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleChatSelect = async (chat) => {
    try {
      let conversationId = chat.conversationId;

      // If no conversation exists, create one
      if (!conversationId) {
        if (chat.type === "team") {
          const conversation = await getOrCreateTeamConversation(
            chat.id,
            token
          );
          conversationId = conversation._id;
        } else {
          const conversation = await getOrCreateDirectConversation(
            chat.id,
            token
          );
          conversationId = conversation._id;
        }
      }

      setSelectedChat({ ...chat, conversationId });

      // Fetch messages for this conversation (backend marks them as read)
      await fetchMessages(conversationId, token);

      // Refresh conversations to get updated unread counts from backend
      await fetchConversations(token);

      // Set as selected conversation
      setSelectedConversation(conversationId);
    } catch (error) {
      console.error("Error selecting chat:", error);
    }
  };

  // Format teams with conversation data
  const teamsWithConversations = teams.map((team) => {
    const conversation = conversations.find(
      (conv) => conv.type === "team" && conv.team?._id === team.id
    );
    return {
      id: team.id,
      name: team.name,
      subtitle: organizationCount > 1 ? team.organizationName : null,
      type: "team",
      unread: conversation?.unread || 0,
      conversationId: conversation?.id,
    };
  });

  // Format members with conversation data
  const membersWithConversations = members.map((member) => {
    const conversation = conversations.find(
      (conv) =>
        conv.type === "direct" &&
        conv.participants?.some((p) => (p._id || p.id) === member.id)
    );
    return {
      id: member.id,
      name: member.name,
      subtitle:
        organizationCount > 1 ? member.organizations?.join(", ") : null,
      type: "member",
      unread: conversation?.unread || 0,
      conversationId: conversation?.id,
    };
  });

  // Get messages for selected chat
  const currentMessages = selectedChat?.conversationId
    ? messages[selectedChat.conversationId] || []
    : [];

  // Format messages for display
  const formattedMessages = currentMessages.map((msg) => {
    const isMe =
      msg.senderId._id === user.id ||
      msg.senderId._id === user._id ||
      msg.senderId === user.id ||
      msg.senderId === user._id;

    return {
      id: msg._id,
      senderId: isMe ? "me" : msg.senderId._id || msg.senderId,
      senderName: isMe
        ? null
        : `${msg.senderId.firstName} ${msg.senderId.lastName}`,
      text: msg.text,
      timestamp: new Date(msg.createdAt).toLocaleTimeString(i18n.language, {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  });

  return (
    <div className="chat-modal-content">
      {/* Modal Header */}
      <div className="title">
        <span>{t("chat.title")}</span>
        <button className="close-button" onClick={onClose}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      {/* Main Wrapper */}
      <div className="main-wrapper">
        {/* Left Sidebar - Teams and Members */}
        <div className="chat-sidebar">
          {/* Teams Section - Only show if there are teams */}
          {teamsWithConversations.length > 0 && (
            <div className="chat-section">
              <h4 className="chat-section-title">{t("chat.teams")}</h4>
              <div className="chat-list">
                {teamsWithConversations.map((team) => (
                  <div
                    key={team.id}
                    className={`chat-item ${selectedChat?.id === team.id ? "active" : ""} ${team.unread > 0 ? "unread" : ""}`}
                    onClick={() => handleChatSelect(team)}
                  >
                    <div className="chat-item-icon">
                      <span className="material-icons">groups</span>
                    </div>
                    <div className="chat-item-info">
                      <div className="chat-item-name">{team.name}</div>
                      {team.subtitle && (
                        <div className="chat-item-subtitle">{team.subtitle}</div>
                      )}
                    </div>
                    {team.unread > 0 && (
                      <div className="chat-item-badge">{team.unread}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Members Section */}
          <div className="chat-section">
            <h4 className="chat-section-title">
              {t("chat.directMessages")}
            </h4>
            <div className="chat-list">
              {membersWithConversations.map((member) => (
                <div
                  key={member.id}
                  className={`chat-item ${selectedChat?.id === member.id ? "active" : ""} ${member.unread > 0 ? "unread" : ""}`}
                  onClick={() => handleChatSelect(member)}
                >
                  <div className="chat-item-icon">
                    <span className="material-icons">person</span>
                  </div>
                  <div className="chat-item-info">
                    <div className="chat-item-name">{member.name}</div>
                    {member.subtitle && (
                      <div className="chat-item-subtitle">{member.subtitle}</div>
                    )}
                  </div>
                  {member.unread > 0 && (
                    <div className="chat-item-badge">{member.unread}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Chat Area */}
        <div className="chat-main">
          {selectedChat ? (
            <>
              {/* Chat Header */}
              <div className="chat-header">
                <div className="chat-header-info">
                  <span className="material-icons">
                    {selectedChat.type === "team" ? "groups" : "person"}
                  </span>
                  <h3>{selectedChat.name}</h3>
                </div>
              </div>

              {/* Messages Area */}
              <div className="chat-messages">
                {formattedMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`message ${msg.senderId === "me" ? "sent" : "received"}`}
                  >
                    {msg.senderId !== "me" && selectedChat.type === "team" && (
                      <div className="message-sender">{msg.senderName}</div>
                    )}
                    <div className="message-content">
                      <div className="message-text">
                        {renderMessageContent(msg.text)}
                      </div>
                      <div className="message-time">{msg.timestamp}</div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="chat-input-area">
                {/* Emoji Picker */}
                {showEmojiPicker && (
                  <div className="picker-popup emoji-picker-popup">
                    <EmojiPicker
                      onEmojiClick={(emojiObject) => {
                        insertEmoji(emojiObject.emoji);
                        setShowEmojiPicker(false);
                      }}
                      width="100%"
                      height="350px"
                    />
                  </div>
                )}

                {/* GIF Picker */}
                {showGifPicker && (
                  <div className="picker-popup gif-picker-popup">
                    <div className="gif-picker-header">
                      <input
                        type="text"
                        placeholder={t("chat.searchGifs")}
                        className="gif-search-input"
                        value={gifSearchTerm}
                        onChange={(e) => setGifSearchTerm(e.target.value)}
                        autoFocus
                      />
                      <button
                        className="close-picker"
                        onClick={() => {
                          setShowGifPicker(false);
                          setGifSearchTerm("");
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div className="gif-grid-container">
                      <Grid
                        width={380}
                        columns={2}
                        key={gifSearchTerm} // Force re-render when search term changes
                        fetchGifs={(offset) =>
                          gifSearchTerm
                            ? gf.search(gifSearchTerm, { offset, limit: 10 })
                            : gf.trending({ offset, limit: 10 })
                        }
                        onGifClick={async (gif, e) => {
                          e.preventDefault();

                          // Send GIF immediately
                          if (selectedChat?.conversationId) {
                            try {
                              const gifMessage = message
                                ? `${message} ${gif.images.original.url}`
                                : gif.images.original.url;

                              await sendMessageToStore(
                                selectedChat.conversationId,
                                gifMessage,
                                token
                              );

                              setMessage("");
                              setShowGifPicker(false);
                              setGifSearchTerm("");
                            } catch (error) {
                              console.error("Error sending GIF:", error);
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                )}

                <form onSubmit={handleSendMessage}>
                  <div className="input-controls">
                    <button
                      type="button"
                      className="control-button"
                      onClick={() => {
                        setShowEmojiPicker(!showEmojiPicker);
                        setShowGifPicker(false);
                      }}
                      title={t("chat.addEmoji")}
                    >
                      <span className="material-icons">mood</span>
                    </button>
                    <button
                      type="button"
                      className="control-button"
                      onClick={() => {
                        setShowGifPicker(!showGifPicker);
                        setShowEmojiPicker(false);
                      }}
                      title={t("chat.addGif")}
                    >
                      <span className="material-icons">gif</span>
                    </button>
                  </div>
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder={t("chat.messagePlaceholder")}
                    value={message}
                    onChange={handleMessageChange}
                    className="chat-input"
                    autoFocus
                  />
                  <button type="submit" className="send-button">
                    <span className="material-icons">send</span>
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="chat-empty-state">
              <span className="material-icons">chat</span>
              <p>{t("chat.selectChat")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
